/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ListChecks, CheckCircle, CalendarBlank, Warning, ShieldCheck,
  Notepad, BellRinging, ArrowRight, FlowArrow,
} from "@phosphor-icons/react";
import { api, formatDate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/app/ui-bits";

// Self-service portal for the employee role. No admin data — only "mine".
export default function EmployeeDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({
    tasks: [], warnings: [], defenses: [], ethics: [],
    notifications: [], calendar: [], loading: true,
  });

  useEffect(() => {
    if (!user) return;
    let alive = true;
    (async () => {
      try {
        const [tasksR, warnR, defR, ethR, notifR, calR] = await Promise.all([
          api.get("/tasks/mine").catch(() => ({ data: [] })),
          api.get("/hr/warnings?staff_email=" + encodeURIComponent(user.email)).catch(() => ({ data: { items: [] } })),
          api.get("/hr/defenses?staff_email=" + encodeURIComponent(user.email)).catch(() => ({ data: { items: [] } })),
          api.get("/ethics-cases?mine=true").catch(() => ({ data: { items: [] } })),
          api.get("/notifications?only_unread=true").catch(() => ({ data: [] })),
          api.get("/calendar/mine").catch(() => ({ data: { events: [] } })),
        ]);
        if (!alive) return;
        const tasks = Array.isArray(tasksR.data) ? tasksR.data : (tasksR.data.items || []);
        const calRaw = calR.data;
        const calendar = Array.isArray(calRaw)
          ? calRaw
          : (calRaw?.events || calRaw?.items || []);
        const notifRaw = notifR.data;
        const notifications = Array.isArray(notifRaw)
          ? notifRaw
          : (notifRaw?.items || []);
        setData({
          tasks,
          warnings: warnR.data.items || warnR.data || [],
          defenses: defR.data.items || defR.data || [],
          ethics: ethR.data.items || ethR.data || [],
          notifications,
          calendar,
          loading: false,
        });
      } catch {
        if (alive) setData((s) => ({ ...s, loading: false }));
      }
    })();
    return () => { alive = false; };
  }, [user]);

  if (data.loading) {
    return <div className="p-10 text-center text-sm text-slate-500">Loading your dashboard…</div>;
  }

  const pendingTasks = data.tasks.filter((t) => !["done", "approved", "cancelled"].includes(t.status));
  const completedTasks = data.tasks.filter((t) => ["done", "approved"].includes(t.status));
  const openWarnings = data.warnings.filter((w) => ["open", "under_review"].includes(w.status));
  const openEthics = data.ethics.filter((e) => ["submitted", "under_review", "in_progress"].includes((e.status || "").toLowerCase()));

  return (
    <div data-testid="employee-dashboard">
      <PageHeader
        title={`Welcome, ${(user?.name || user?.email || "").split(" ")[0]}`}
        description="Your personal workspace — only your records, calendar, tasks, warnings and reports."
      />
      <div className="p-5 md:p-8 space-y-5">
        {/* KPI strip */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
          <Tile testid="emp-kpi-pending-tasks" icon={ListChecks} label="Pending tasks" value={pendingTasks.length} tone={pendingTasks.length ? "blue" : "slate"} onClick={() => navigate("/tasks")} />
          <Tile testid="emp-kpi-completed-tasks" icon={CheckCircle} label="Completed tasks" value={completedTasks.length} tone="emerald" onClick={() => navigate("/tasks")} />
          <Tile testid="emp-kpi-calendar" icon={CalendarBlank} label="Calendar items" value={data.calendar.length} tone="indigo" onClick={() => navigate("/calendar")} />
          <Tile testid="emp-kpi-warnings" icon={Warning} label="Open warnings" value={openWarnings.length} tone={openWarnings.length ? "amber" : "slate"} />
          <Tile testid="emp-kpi-defenses" icon={ShieldCheck} label="My defenses" value={data.defenses.length} tone="slate" />
          <Tile testid="emp-kpi-ethics" icon={Notepad} label="Open ethics reports" value={openEthics.length} tone={openEthics.length ? "violet" : "slate"} onClick={() => navigate("/ethics")} />
        </div>

        {/* Two-column area */}
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          {/* My pending tasks */}
          <Card title="My Pending Tasks" cta={{ label: "Open all tasks", to: "/tasks" }} icon={ListChecks}>
            {pendingTasks.length === 0
              ? <Empty>No pending tasks. You're all caught up.</Empty>
              : (
                <ul className="divide-y divide-slate-100">
                  {pendingTasks.slice(0, 6).map((t) => (
                    <li key={t.id} className="px-4 py-3 hover:bg-slate-50 cursor-pointer flex items-center gap-3"
                        onClick={() => navigate(`/tasks?open=${t.id}`)} data-testid={`emp-task-${t.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm text-slate-900 truncate">{t.title}</div>
                        <div className="text-[11px] text-slate-500">
                          {t.due_date && `Due ${formatDate(t.due_date)} · `}
                          {(t.status || "").replace(/_/g, " ")}
                        </div>
                      </div>
                      <ArrowRight size={14} className="text-slate-400" />
                    </li>
                  ))}
                </ul>
              )}
          </Card>

          {/* My calendar */}
          <Card title="Upcoming Calendar Items" cta={{ label: "Open calendar", to: "/calendar" }} icon={CalendarBlank}>
            {data.calendar.length === 0
              ? <Empty>No upcoming events.</Empty>
              : (
                <ul className="divide-y divide-slate-100">
                  {data.calendar.slice(0, 6).map((c) => (
                    <li key={c.id} className="px-4 py-3 text-sm">
                      <div className="font-semibold text-slate-900">{c.title}</div>
                      <div className="text-[11px] text-slate-500">
                        {formatDate(c.start_at || c.date)}{c.location && ` · ${c.location}`}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
          </Card>

          {/* My warnings */}
          <Card title="My Warnings" icon={Warning}>
            {data.warnings.length === 0
              ? <Empty>No warnings on file — clean record.</Empty>
              : (
                <ul className="divide-y divide-slate-100">
                  {data.warnings.slice(0, 5).map((w) => (
                    <li key={w.id} className="px-4 py-3 text-sm">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-semibold text-slate-900">{(w.warning_type || "").replace(/_/g, " ")}</div>
                        <span className="rounded bg-amber-50 border border-amber-200 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-amber-800">{w.status}</span>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{formatDate(w.issue_date)} · {(w.reason || "").slice(0, 90)}</div>
                    </li>
                  ))}
                </ul>
              )}
          </Card>

          {/* My defense responses */}
          <Card title="My Defense Responses" icon={ShieldCheck}>
            {data.defenses.length === 0
              ? <Empty>You have not submitted any defense responses.</Empty>
              : (
                <ul className="divide-y divide-slate-100">
                  {data.defenses.slice(0, 5).map((d) => (
                    <li key={d.id} className="px-4 py-3 text-sm">
                      <div className="font-semibold text-slate-900">Defense · {formatDate(d.response_date)}</div>
                      <div className="text-[11px] text-slate-500">{(d.response || "").slice(0, 140)}</div>
                    </li>
                  ))}
                </ul>
              )}
          </Card>

          {/* My ethics reports */}
          <Card title="My Ethics & Grievance Reports" cta={{ label: "Submit a new report", to: "/ethics" }} icon={Notepad}>
            {data.ethics.length === 0
              ? <Empty>You have not filed any ethics or grievance reports.</Empty>
              : (
                <ul className="divide-y divide-slate-100">
                  {data.ethics.slice(0, 5).map((e) => (
                    <li key={e.id} className="px-4 py-3 text-sm">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-semibold text-slate-900">{e.title || "(no title)"}</div>
                        <span className="rounded bg-violet-50 border border-violet-200 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-violet-800">{e.status}</span>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{e.category} · filed {formatDate(e.created_at)}</div>
                    </li>
                  ))}
                </ul>
              )}
          </Card>

          {/* My notifications */}
          <Card title="My Notifications" icon={BellRinging}>
            {data.notifications.length === 0
              ? <Empty>No new notifications.</Empty>
              : (
                <ul className="divide-y divide-slate-100">
                  {data.notifications.slice(0, 5).map((n) => (
                    <li key={n.id} className="px-4 py-3 text-sm">
                      <div className="font-semibold text-slate-900">{n.title || n.message}</div>
                      <div className="text-[11px] text-slate-500">{n.body || ""}{n.created_at && ` · ${formatDate(n.created_at)}`}</div>
                    </li>
                  ))}
                </ul>
              )}
          </Card>
        </div>

        <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-[11px] text-slate-600 flex items-start gap-2">
          <FlowArrow size={14} className="text-slate-400 shrink-0 mt-0.5" />
          <span>
            This is your personal workspace. Company-wide records (HR admin, finance, procurement,
            other employees' data) are not shown — only what is yours.
          </span>
        </div>
      </div>
    </div>
  );
}

function Tile({ testid, icon: Icon, label, value, tone = "slate", onClick }) {
  const tones = {
    blue: "border-l-[#1E5CBA]", emerald: "border-l-emerald-500",
    amber: "border-l-amber-500", indigo: "border-l-indigo-500",
    violet: "border-l-violet-500", slate: "border-l-slate-300",
  };
  return (
    <button
      type="button" onClick={onClick} disabled={!onClick}
      data-testid={testid}
      className={`dc-card p-3 border-l-4 ${tones[tone]} text-left hover:shadow-md transition disabled:cursor-default disabled:hover:shadow-none`}
    >
      <div className="flex items-start gap-2">
        <Icon size={16} className="text-slate-500" />
        <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">{label}</div>
      </div>
      <div className="mt-1 font-display text-2xl font-semibold text-slate-900 tabular-nums">{value}</div>
    </button>
  );
}

function Card({ title, cta, icon: Icon, children }) {
  const navigate = useNavigate();
  return (
    <div className="dc-card overflow-hidden">
      <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-4 py-3">
        <div className="flex items-center gap-2">
          {Icon && <Icon size={16} className="text-[#1E5CBA]" />}
          <h3 className="font-display text-[14px] font-semibold text-slate-900">{title}</h3>
        </div>
        {cta && (
          <Button size="sm" variant="ghost" className="text-xs text-[#1E5CBA] hover:bg-[#1E5CBA]/5"
                  onClick={() => navigate(cta.to)}>
            {cta.label} <ArrowRight size={12} className="ml-1" />
          </Button>
        )}
      </div>
      {children}
    </div>
  );
}

function Empty({ children }) {
  return <div className="p-6 text-center text-sm text-slate-400">{children}</div>;
}
